import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { isEmbedded } from '../lib/embedMode';

export interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  isAdmin: boolean;
  selectedProjectId?: string | null;
  createdAt: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authError: string | null;
  login: () => void;
  logout: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
  clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Stand-in identity for an embedded session so the app renders past its
// isAuthenticated gate. Carries no personal data - none crosses the CC
// boundary (COM-636) - and `isAdmin` stays false: embed rights are enforced
// server-side per project, never by this flag.
const EMBEDDED_USER: User = {
  id: 'embedded-cc-session',
  email: '',
  name: '',
  isAdmin: false,
  createdAt: new Date(0).toISOString(),
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  const isAuthenticated = !!user;

  useEffect(() => {
    const storedError = localStorage.getItem('auth_error');
    if (storedError) {
      setAuthError(storedError);
      localStorage.removeItem('auth_error');
    }
  }, []);

  const clearAuthError = () => {
    setAuthError(null);
  };

  const checkAuthStatus = async () => {
    // Embedded (CC iframe) sessions are established by the bootstrap
    // handshake, not by a cookie: /auth/me would always 401 here, and there
    // is no RS user behind an embed session at all (COM-636).
    if (isEmbedded()) {
      setUser(EMBEDDED_USER);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || ""}/auth/me`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          setUser(data.user);
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth status check failed:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = () => {
    window.location.href = `${process.env.REACT_APP_API_URL || ""}/auth/google`;
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || ""}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        setUser(null);
        window.location.href = '/';
      } else {
        console.error('Logout failed');
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authStatus = urlParams.get('auth');
    const errorType = urlParams.get('error');
    
    if (authStatus === 'success') {
      window.history.replaceState({}, document.title, window.location.pathname);
      checkAuthStatus();
    } else if (errorType) {
      console.error('Authentication error:', errorType);
      window.history.replaceState({}, document.title, window.location.pathname);

      setAuthError(errorType);

      setUser(null);
      setIsLoading(false);
    }
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    authError,
    login,
    logout,
    checkAuthStatus,
    clearAuthError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 